import { Component } from '@angular/core';
import { TrpcService } from '../../services/trpc/trpc.service';

import { MessageService } from 'primeng/api';
import { FileSelectEvent } from 'primeng/fileupload';

import * as xlsx from 'xlsx';
import { environment } from '../../../environments/environment';
import { Title } from '@angular/platform-browser';
import { AccessCheckResult } from '../../../../../express-tRPC/src/invoice-library-shared/cross-domain-types';
import { DailySummarySettings } from '../../../../../express-tRPC/src/api/lot-settings/types';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from 'src/app/services/auth/auth.service';
import { finalize } from 'rxjs';

const COMPONENT_FEATURE_NAME = 'SUREPARK Daily Summary Import';

@Component({
  selector: 'app-import-daily-summary',
  templateUrl: './import-daily-summary.component.html',
  styleUrl: './import-daily-summary.component.scss'
})
export class ImportDailySummaryComponent {

  version = environment.version;
  activeFile = environment.activeFile;

  supportedCSVMimeTypes: string = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/msexcel",
    "application/x-msexcel",
    "application/x-ms-excel",
    "application/x-excel",
    "application/x-dos_ms_excel",
    "application/xls",
    "application/x-xls"
  ].join(",");

  supportedPDFMimeTypes: string = [
    "application/pdf"
  ].join(",");

  // Used during development to speed up testing. Not needed during production
  // These can be generated by opening the surepark XLSx file and then base64 encoding the contents
  // See embedded-sample-files.txt

  // Indicates that the embedded test file has or hasn't been loaded.
  testFileLoaded = false;

  loading: boolean = true;
  importedSheetData: any = undefined;
  fileImportData: any = [];

  dailySummaryData: any = {
    columns: [
      { field: 'shift', header: 'Parkpro Shift	', prefix: 'Shift-' },
      { field: 'cashier', header: 'Cashier' },
      { field: 'tix_collected', header: 'Tickets Collected' },
      { field: 'amount', header: 'Amount' },
      { field: 'cc_fee', header: 'CC Fee' },
      { field: 'lost_tix', header: 'Lost Tickets' },
      { field: 'stations', header: 'Station(s)' },
    ],
    rows: []
  }

  importSummaryInProgress: boolean = false;
  isSingleLotSummary: boolean = false;

  currentLocation: string = '';

  // Most of the locations are single shift even if they have multiple lots. There is only one location that has 5 shifts
  // so as long as the number of lots is less than this we can assume it's a single shift location.
  NUMBER_OF_LOTS_TO_ACTIVATE_MULTISHIFT = 5;

  /*
  This contains a mapping of the lot names to shift numbers. By default the shift will be 1 unless found in the mappings
   */
  lotShiftMappping: any[] = [
    { lot: 1549, shift: 1 },
    { lot: 1515, shift: 2 },
    { lot: 4950, shift: 3 },
    { lot: 4700, shift: 4 },
    { lot: 4760, shift: 5 },
    // These are the shifts for location 284
    // { lot: 'Loc 284', shift: 1 },
    // { lot: 'Sycamore', shift: 2 }
  ]

  // Indicates whether the settings are being loaded
  loadingSettings: boolean = false;
  // Indicates if there was an error loading settings
  settingsError: boolean = false;

  // This is the default report type which will get overwritten by the settings when loaded.
  dailySummaryReportType: string = "unknown";
  dailySummaryCalculateRevenueMethod: string = "Use Validated and Non-Validated Income";
  dailySummaryDontReportCreditCardFees: boolean = false;
  dailySummaryDontTrackLostTickets: boolean = false;
  dailySummaryReportCCFeesToTicketsCollected: boolean = false;
  dailySummaryReportMultiShiftEnabled: boolean = false;

  selectedFileBlobData: File | undefined = undefined;

  constructor(private trpcService: TrpcService, private messageService: MessageService, private titleService: Title, private authService: AuthService, private httpClient: HttpClient) { }

  get getImortSheetLabel() {
    if (this.dailySummaryReportType == 'surepark') {
      return "Import Daily Summary Sheet";
    } else if (this.dailySummaryReportType == 'cashier') {
      return 'Import Cashier Summary Sheet';
    } else if (this.dailySummaryReportType == 'wps') {
      return 'Import WPS Summary Sheet';
    } else if (this.dailySummaryReportType == 'syspark') {
      return 'Import Syspark Summary Sheet';
    }

    return this.dailySummaryReportType;

  }

  get getSupportedMimeTypes() {
    if (this.dailySummaryReportType == 'wps' || this.dailySummaryReportType == 'syspark') {
      return this.supportedPDFMimeTypes;
    }

    return this.supportedCSVMimeTypes;
  }

  ngOnInit() {

    // this.testDev();

    this.trpcService.currentLocationObservable.subscribe((location: string) => {
      this.currentLocation = location;
      this.titleService.setTitle(`${location} | Import Daily Summary`);


      //
      // For testing an embedded file can automatically be loaded on component load by uncommenting a test plan below
      //
      //this.importTestMultilotSUREPARKFile(this.embeddedSingleshiftSUREPARKFile);
      //this.importTestMultilotSUREPARKFile(this.embeddedMultishiftSUREPARKFile);
      //this.importTestMultilotSUREPARKFile(this.embeddedNoShiftSUREPARKFile);
      //this.importTestMultilotSUREPARKFile(this.embeddedTwoShiftSUREPARKFile);
    })

    this.checkAccessToFeature();
    this.getSettings();
  }

  async getSettings() {

    this.loadingSettings = true;

    let dailySummarySettings: DailySummarySettings | void = await this.trpcService.trpc.settings.getDailySummarySettings.query().catch(async (error: any) => {
      // Track settings load failure
      try {
        await this.trpcService.trpc.lots.trackDailySummaryEvent.mutate({
          eventType: 'settings_load_failed',
          metadata: {
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
            appVersion: environment.version
          }
        });
      } catch (trackingError) {
        console.error('Failed to track settings error event:', trackingError);
      }

      this.loadingSettings = false;
      this.settingsError = true;
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    if (dailySummarySettings) {
      this.dailySummaryReportType = dailySummarySettings.daily_summary_report_type || "surepark";
      this.dailySummaryCalculateRevenueMethod = dailySummarySettings.daily_summary_calculate_revenue_method || "Use Validated and Non-Validated Income";
      this.dailySummaryDontReportCreditCardFees = dailySummarySettings.daily_summary_dont_report_cc_fees || false;
      this.dailySummaryDontTrackLostTickets = dailySummarySettings.daily_summary_dont_track_lost_tickets || false;
      this.dailySummaryReportCCFeesToTicketsCollected = dailySummarySettings.daily_summary_report_cc_fees_to_tickets_collected || false;
      this.dailySummaryReportMultiShiftEnabled = dailySummarySettings.daily_summary_multi_shift_enabled || false;

      //We have to set conditions based on whether ticket summary has single lot or multiple
      this.isSingleLotSummary = !this.dailySummaryReportMultiShiftEnabled;

      this.settingsError = false;
    } else {
      // Settings are empty or failed to load
      this.settingsError = true;
      this.messageService.add({
        severity: "error",
        detail: "Failed to load daily summary settings. Please check your configuration.",
      });
    }

    this.loadingSettings = false;

  }

  async importSheet(event: FileSelectEvent) {
    this.loading = true;
    // console.log(`Importing file:`, event.files[0]);

    // Capture the file reference immediately
    const file = event.files[0];
    if (!file) {
      console.error('No file selected');
      this.loading = false;
      return;
    }

    // Track the event
    try {
      await this.trpcService.trpc.lots.trackDailySummaryEvent.mutate({
        eventType: 'file_selected',
        metadata: {
          fileCount: event.files.length,
          fileName: file.name,
          fileSize: file.size,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          appVersion: environment.version
        }
      });
    } catch (error) {
      // Don't let tracking errors prevent the import
      console.error('Failed to track event:', error);
    }

    // Use the captured file reference
    this.importSUREPARKFile(file);
  }

  /**
   * Given a Base64 Encoded XLSX file it will automatically import the data without having to select a file and click
   * the import button. Useful during development to keep resetting the state between recompiles.
   *
   * @param base64EncodedXLSXFile
   */
  async importTestMultilotSUREPARKFile(base64EncodedXLSXFile: string) {
    // console.log(`importTestMultilotSUREPARKFile running...`);
    this.loading = true;

    const fileBuffer = Uint8Array.from(atob(base64EncodedXLSXFile), c => c.charCodeAt(0)).buffer;

    this.testFileLoaded = true;
    this.readAndParseSUREPARKDailySummaryFile(fileBuffer);
  }

  async importSUREPARKFile(blob: File) {
    const fileBuffer = await blob.arrayBuffer();
    console.log("fileBuffer", fileBuffer)
    this.readAndParseSUREPARKDailySummaryFile(fileBuffer, blob);
  }

  async readAndParseSUREPARKDailySummaryFile(data: ArrayBuffer, file: File | undefined = undefined) {
    
    this.selectedFileBlobData = file;

    if (this.dailySummaryReportType == 'wps' || this.dailySummaryReportType == 'syspark') {

      try {
        if (file) {

          const formData = new FormData();
          formData.append("file", file);

          const url = environment.NODE_ENDPOINT_URL + 'extract-daily-summary-pdf-file';

          let httpOptions = new HttpHeaders({
            'Accept': "multipart/form-data",
            'Authorization': this.authService.cookieData?.cookie.value || ''
          });

          this.httpClient.post(url, formData, { headers: httpOptions }).pipe(finalize(() => {
            this.loading = false;
          })).subscribe(
            (pdfData: any) => {
              this.importedSheetData = pdfData?.['data'];
            },
            (error: any) => {
              this.messageService.add({
                severity: "error",
                detail: error.message || 'Failed to extract PDF data'
              });
            }
          );

        }
      } catch (error) {
        console.error('Failed to extract pdf:', error);
        this.messageService.add({
          severity: "error",
          detail: "Failed to extract pdf.",
        });
      }

    } else {
      try {
        const workBook = xlsx.read(data, { type: 'buffer', cellNF: true, cellText: false });

        let worksheet = workBook.Sheets[workBook.SheetNames[0]];

        worksheet = structuredClone(this.updateWorksheetCellFormat(worksheet));

        let worksheetData: any = xlsx.utils.sheet_to_json(worksheet, { raw: false, defval: '' });

        if (worksheetData?.length && this.dailySummaryReportType == 'surepark') {

          //As per sheet format second row would be column
          let ticketSummaryRowIndex: number[] = [];
          let revenueRowIndex: number[] = [];
          let lostTixRowIndex: number[] = [];
          let nonValidatedByLotRowIndex: number[] = [];

          if (this.isSingleLotSummary) {
            const isNonValidatedTableExist = this.checkTableExist("non-validated", worksheetData);
            if (!isNonValidatedTableExist) {
              throw new Error("Non-Validated table not found in this sheet. its required if multi shift setting is off.");
            }
          } else {
            const isNonValidatedByLotTableExist = this.checkTableExist("non-validated by lot", worksheetData);
            if (!isNonValidatedByLotTableExist) {
              throw new Error("Non-Validated By Lot table not found in this sheet. its required if multi shift setting is on.");
            }
          }

          // Loop through all the rows in the sheet
          worksheetData.forEach((worksheetDataRowsObj: any, rowIndex: number) => {

            // Loop through each column in the row
            Object.keys(worksheetDataRowsObj).forEach((key: any, columnIndex: number) => {

              // Get the current cell data for the current row
              const cellDataLowerCased: string = worksheetDataRowsObj[key].toLowerCase();
              // console.log(`Scanning row ${rowIndex} col ${key}:`, cellDataLowerCased);

              if (cellDataLowerCased.includes("date: ")) {
                const date: string = cellDataLowerCased.split("date: ")[1];
                this.fileImportData.push({
                  date: date
                })
              }
              if (cellDataLowerCased.includes("ticket summary")) {
                ticketSummaryRowIndex.push(rowIndex);
              }

              if (cellDataLowerCased.includes("revenue")) {
                ticketSummaryRowIndex.push(rowIndex)
                revenueRowIndex.push(rowIndex);
              }

              if (cellDataLowerCased.includes("subscriber")) {
                revenueRowIndex.push(rowIndex);
              }
              if (cellDataLowerCased.includes("lost tickets")) {
                lostTixRowIndex.push(rowIndex);
              }

              //We will consider "non-validated" table for single lot import sheet and "day pass" table for multiple lot import sheet
              //Because non-validated can appear below day pass we want to be sure to only break if we haven't found the ending
              // for the lost tickets section.
              if (lostTixRowIndex.length == 1 && (cellDataLowerCased.includes("day pass") || cellDataLowerCased.includes("non-validated"))) {
                lostTixRowIndex.push(rowIndex);
              }

              //We will consider "non-validated" table for single lot import sheet
              if (this.isSingleLotSummary && cellDataLowerCased == "non-validated") {
                nonValidatedByLotRowIndex = [rowIndex, worksheetData.length];
              }

              //"non-validated by lot" table for multiple lot import sheet
              if (!this.isSingleLotSummary && cellDataLowerCased.includes("non-validated by lot")) {
                nonValidatedByLotRowIndex = [rowIndex, worksheetData.length];
              }

              //Make sure we have start and end index so we can split "ticket summary" data from sheet
              if (ticketSummaryRowIndex.length == 2) {
                const sheetData = structuredClone(worksheetData);
                let columnNames: any[] = [];
                let ticketSummaryData = sheetData.slice(ticketSummaryRowIndex[0] + 1, ticketSummaryRowIndex[1]);
                Object.keys(ticketSummaryData[0]).forEach((columnKey: any) => {
                  if (ticketSummaryData[0][columnKey]) {
                    columnNames.push({
                      field: ticketSummaryData[0][columnKey].toLowerCase().replaceAll(" ", "_"),
                      header: ticketSummaryData[0][columnKey],
                      original_key: columnKey
                    });
                  }
                })

                // If the lost ticket start has been located then once we see the total in the expected column we know we've found the end
                // if (lostTixRowIndex.length == 1 && cellDataLowerCased.includes("total:") && key === '__EMPTY_7') {
                //   console.log(`Found end of lost tickets table`);
                //   lostTixRowIndex.push(rowIndex);
                // }

                let ticketSummaryDataRows = ticketSummaryData.slice(1, ticketSummaryData.length - 1);

                // This is the name of the last lot that we may have processed. Assume an empty string means no last lot processed.
                let lastLotName: string = '';

                //Map column array with data. This is the 'Ticket Summary' section of the sheet.
                let worksheetDataRowsUpdatedKeys = ticketSummaryDataRows.map((rowData: any, rowIndex: number) => {
                  let row: any = {};
                  Object.keys(rowData).forEach((rowDataKeys: string, index: number) => {
                    const matchedColumnIndex = columnNames.findIndex((columnData: any) => columnData.original_key == rowDataKeys);

                    if (matchedColumnIndex !== -1) {
                      const columnNameBeingProcessesd = columnNames[matchedColumnIndex]['field'];
                      if (columnNameBeingProcessesd == "lot_name" && rowData[rowDataKeys]) {
                        lastLotName = rowData[rowDataKeys];
                      }
                      if (columnNameBeingProcessesd == "lot_name" && rowData[rowDataKeys] == '') {
                        row[columnNameBeingProcessesd] = lastLotName;
                      } else {
                        row[columnNameBeingProcessesd] = rowData[rowDataKeys];
                      }
                      if (columnNameBeingProcessesd == "lot_name") {
                        // We are processing the column that contains the name of the lot

                        // Store the shift number for this lot. Assume that we can't find a lot shift so set it to null
                        let lot_shift: number | null = null;

                        // Map the lot name to a shift.
                        if (this.isSingleLotSummary) {
                          // Because we only have one lot the shift will always be 1
                          lot_shift = 1;
                        } else {
                          // We have more than one lot so find the shift in the mapping
                          const lotDetails = this.lotShiftMappping.find((lot: any) => {

                            //console.info(`Checking if ${lot['lot']} matches ${lastLotName}`);

                            if (lot['lot'] == lastLotName) {
                              return true;
                            } else {
                              return false;
                            }
                          });

                          // At this point we should have details about the lot name if we have a location with multiple shifts

                          if (!lotDetails) {
                            // We couldn't find a shift for this lot so default to shift 1
                            // If there was supposed to be a shift them make sure the lot name and shift are in lotShiftMapping

                            lot_shift = 1;

                          } else {
                            lot_shift = lotDetails['shift'];
                          }
                        }

                        console.log(`Resolved lot ${lastLotName} to ${lot_shift}`);

                        row['shift'] = lot_shift;
                      }
                    }
                  })
                  return row;
                });

                //Extract summary row and map with column
                let ticketSummaryDataSummaryRow = ticketSummaryData.slice(ticketSummaryData.length - 1, ticketSummaryData.length);
                let worksheetDataSummaryRowUpdatedKeys = ticketSummaryDataSummaryRow.map((rowData: any, rowIndex: number) => {
                  let row: any = {};
                  Object.keys(rowData).forEach((rowDataKeys: string, index: number) => {
                    const matchedColumnIndex = columnNames.findIndex((columnData: any) => columnData.original_key == rowDataKeys);
                    if (matchedColumnIndex !== -1) {
                      if (columnNames[matchedColumnIndex]['field'] == "lot_name" && rowData[rowDataKeys]) {
                        lastLotName = rowData[rowDataKeys];
                      }
                      if (columnNames[matchedColumnIndex]['field'] == "lot_name" && rowData[rowDataKeys] == '') {
                        row[columnNames[matchedColumnIndex]['field']] = lastLotName;
                      } else {
                        row[columnNames[matchedColumnIndex]['field']] = rowData[rowDataKeys];
                      }
                    }
                  });

                  return row;
                });

                this.fileImportData[this.fileImportData.length - 1]['ticket_summary'] = { columns: columnNames, rows: worksheetDataRowsUpdatedKeys, summary: worksheetDataSummaryRowUpdatedKeys };
                ticketSummaryRowIndex = [];
              }

              //Make sure we have start and end index so we can split "revenue" data from sheet
              if (revenueRowIndex.length == 2) {
                const sheetData = structuredClone(worksheetData);

                let columnNames: any[] = [];
                let revenueData = sheetData.slice(revenueRowIndex[0] + 1, revenueRowIndex[1]);
                Object.keys(revenueData[0]).forEach((columnKey: any) => {
                  if (revenueData[0][columnKey]) {
                    columnNames.push({ field: revenueData[0][columnKey].toLowerCase().replaceAll(" ", "_"), header: revenueData[0][columnKey], original_key: columnKey });
                  }
                })
                let revenueDataRows = revenueData.slice(1, revenueData.length - 1);
                let lastPaymentType: string = '';

                //Map column array with data
                let worksheetDataRowsUpdatedKeys = revenueDataRows.map((rowData: any) => {
                  let row: any = {};
                  Object.keys(rowData).forEach((rowDataKeys: string, index: number) => {
                    const matchedColumnIndex = columnNames.findIndex((columnData: any) => columnData.original_key == rowDataKeys);
                    if (matchedColumnIndex !== -1) {

                      if (columnNames[matchedColumnIndex]['field'] == "payment_type" && rowData[rowDataKeys]) {
                        lastPaymentType = rowData[rowDataKeys];
                      }
                      if (columnNames[matchedColumnIndex]['field'] == "payment_type" && rowData[rowDataKeys] == '') {
                        row[columnNames[matchedColumnIndex]['field']] = lastPaymentType;
                      } else {
                        row[columnNames[matchedColumnIndex]['field']] = rowData[rowDataKeys];
                      }

                    }
                  })
                  return row;
                });
                // Helper function to check if a lot is a payment summary row
                // Checks for both singular and plural forms: "payment:" and "payments:"
                const isPaymentSummaryRow = (lotText: string): boolean => {
                  return lotText.includes("Credit Card payment:") || lotText.includes("Credit Card payments:") ||
                    lotText.includes("Cash payment:") || lotText.includes("Cash payments:");
                };

                // Filter out summary rows to get regular revenue data
                const worksheetDataRowsUpdatedRows = worksheetDataRowsUpdatedKeys.filter((rowsData: any) => {
                  return !isPaymentSummaryRow(rowsData['lot']);
                });

                // Extract only the summary rows (Credit Card and Cash payment totals)
                const worksheetDataSummaryRows = worksheetDataRowsUpdatedKeys.filter((rowsData: any) => {
                  return isPaymentSummaryRow(rowsData['lot']);
                });
                this.fileImportData[this.fileImportData.length - 1]['revenue'] = { columns: columnNames, rows: worksheetDataRowsUpdatedRows, summary: worksheetDataSummaryRows };
                revenueRowIndex = [];
              }

              //Make sure we have start and end index so we can split "lost tickets" data from sheet
              if (lostTixRowIndex.length == 2) {
                // console.log(`lostTixRowIndex running on segment...`, lostTixRowIndex);

                const sheetData = structuredClone(worksheetData);

                let columnNames: any[] = [];
                let lostTixRowData = sheetData.slice(lostTixRowIndex[0] + 1, lostTixRowIndex[1]);

                Object.keys(lostTixRowData[0]).forEach((columnKey: any, columnIndex: number) => {
                  //We have 2 table in single row so we need to skip first table
                  //Skip "Suscribers" table and consider "Lost Tickets" table after 7th column index

                  let skipColumns: number = this.isSingleLotSummary ? 5 : 7;

                  if (lostTixRowData[0][columnKey] && columnIndex > skipColumns) {
                    columnNames.push({
                      field: lostTixRowData[0][columnKey].toLowerCase().replaceAll(" ", "_"),
                      header: lostTixRowData[0][columnKey],
                      original_key: columnKey
                    });
                  }
                });

                let lostTixRowDataFiltered: any[] = [];

                lostTixRowData.forEach((rowData: any, rowIndex: number) => {
                  let data: any = {};

                  // Do we have any data in this row?
                  if (Object.values(rowData).join('') !== '') {
                    // Yes, we have some data

                    Object.keys(rowData).forEach((columnKey: any, columnIndex: number) => {
                      //We have 2 table in single row so we need to skip first table
                      //Skip "Suscribers" table and consider "Lost Tickets" table after 7th column index
                      let skipColumns: number = this.isSingleLotSummary ? 5 : 7;

                      if (columnIndex > skipColumns) {
                        data[columnKey] = rowData[columnKey];
                      }
                    });
                    lostTixRowDataFiltered.push(data);
                  }
                });
                lostTixRowData = lostTixRowDataFiltered;

                let lostTixRows = lostTixRowData.slice(1, lostTixRowData.length);
                let worksheetDataRowsUpdatedKeys: any[] = [];

                //Map column array with data
                lostTixRows.forEach((rowData: any) => {
                  let row: any = {};

                  //Check if row is not blank
                  if (Object.values(rowData).join('') !== '') {
                    Object.keys(rowData).forEach((rowDataKeys: string, index: number) => {
                      const matchedColumnIndex = columnNames.findIndex((columnData: any) => columnData.original_key == rowDataKeys);
                      if (matchedColumnIndex !== -1) {
                        const rowDataValue = rowData[rowDataKeys];
                        // console.log(`Found rowDataValue: ${rowDataValue} for column: ${columnNames[matchedColumnIndex]['field']}`);
                        row[columnNames[matchedColumnIndex]['field']] = rowDataValue;
                      }
                    })

                    if (!row['value']) {
                      // Normalize the data to ensure the required property is always present
                      row['value'] = '';
                    }
                    worksheetDataRowsUpdatedKeys.push(row);
                  }
                });

                // console.log(`worksheetDataRowsUpdatedKeys after lost ticket search`, lostTixRows);

                const worksheetDataRowsUpdatedRows = worksheetDataRowsUpdatedKeys.filter((rowsData: any) => {
                  // Return all rows that DO NOT have total.
                  return !rowsData['value'].includes("Total:");
                });
                const worksheetDataSummaryRows = worksheetDataRowsUpdatedKeys.filter((rowsData: any) => {
                  // Return all rows that DO have total
                  return rowsData['value'].includes("Total:");
                });

                const lostTicketSummaryEntry = {
                  columns: columnNames,
                  rows: worksheetDataRowsUpdatedRows,
                  summary: worksheetDataSummaryRows
                };

                console.log(`lostTicketSummaryEntry`, lostTicketSummaryEntry);
                //console.log(`Adding lost ticket summary`, worksheetDataSummaryRows);

                this.fileImportData[this.fileImportData.length - 1]['lost_tickets'] = lostTicketSummaryEntry;
                lostTixRowIndex = [];
              }

              //Make sure we have start and end index so we can split "Non-Validated by Lot" data from sheet
              if (nonValidatedByLotRowIndex.length == 2) {

                const sheetData = structuredClone(worksheetData);

                let nonValidatedByLotColumnNames: any[] = [];
                let validatedByLotColumnNames: any[] = [];

                //Remove column row (first row)
                let nonValidatedByLotRowData = sheetData.slice(nonValidatedByLotRowIndex[0] + 1, nonValidatedByLotRowIndex[1]);

                Object.keys(nonValidatedByLotRowData[0]).forEach((columnKey: any, columnIndex: number) => {
                  //We have 2 table in single row so we need to skip second table
                  //Skip "Validated by Lot" table and consider "Non-Validated by Lot" table
                  if (nonValidatedByLotRowData[0][columnKey]) {

                    /**
                     * We have different no of columns for "Non-Validated by Lot" and "Validated by Lot"
                     * because we have tables side by side we have to define no of columns for first table
                     * Here we have checked if we have single lot sheet
                     * if TRUE then we will have 3 columns in "Non-Validated" Table
                     * If False then we will have 4 columns in "Non-Validated by Lot" Table
                     **/


                    let noOfcolumns: number = this.isSingleLotSummary ? 3 : 4;

                    //Get first table column data
                    if (nonValidatedByLotColumnNames.length < noOfcolumns) {
                      nonValidatedByLotColumnNames.push({
                        field: nonValidatedByLotRowData[0][columnKey].toLowerCase().replaceAll(" ", "_"),
                        header: nonValidatedByLotRowData[0][columnKey],
                        original_key: columnKey
                      });
                    } else if (nonValidatedByLotColumnNames.length >= noOfcolumns) {
                      //Get second table column data
                      validatedByLotColumnNames.push({
                        field: nonValidatedByLotRowData[0][columnKey].toLowerCase().replaceAll(" ", "_"),
                        header: nonValidatedByLotRowData[0][columnKey],
                        original_key: columnKey
                      });
                    }
                  }
                });

                let nonValidatedByLotRowDataFiltered: any[] = [];
                let validatedByLotRowDataFiltered: any[] = [];

                for (let index = 0; index < nonValidatedByLotRowData.length; index++) {
                  const rowData = nonValidatedByLotRowData[index];

                  /**
                   *
                   * If multi shift setting is on but if sheet have both tables
                   * "Non-Validated" and "Non-Validated by Lot" table
                   * current logic will consider both tables in single object
                   * we dont need only one table data
                   * so we have break row loop if "Non-Validated by Lot" table data found 
                   */
                  if (this.isSingleLotSummary && Object.values(rowData).join('').includes("Non-Validated by Lot")) {
                    break;
                  }

                  let nonValidatedByLotRow: any = undefined;
                  let validatedByLotRow: any = undefined;

                  nonValidatedByLotColumnNames.forEach((column: any, columnIndex) => {

                    if (!nonValidatedByLotRow) {
                      nonValidatedByLotRow = {};
                    }
                    nonValidatedByLotRow[column['original_key']] = rowData[column['original_key']];

                  });
                  validatedByLotColumnNames.forEach((column: any, columnIndex) => {

                    if (!validatedByLotRow) {
                      validatedByLotRow = {};
                    }
                    validatedByLotRow[column['original_key']] = rowData[column['original_key']];

                  });
                  if (this.isRowNotEmpty(nonValidatedByLotRow)) {
                    nonValidatedByLotRowDataFiltered.push(nonValidatedByLotRow);
                  }
                  if (this.isRowNotEmpty(validatedByLotRow)) {
                    validatedByLotRowDataFiltered.push(validatedByLotRow);
                  }

                  // let nonValidatedByLotRow: any = undefined;
                  // let validatedByLotRow: any = undefined;

                  // //Dont include empty rows
                  // if (Object.values(rowData).join('') !== '') {
                  //   Object.keys(rowData).forEach((columnKey: any, columnIndex: number) => {
                  //     //We have 2 table in single row so we need to skip first table
                  //     //Skip "Suscribers" table and consider "Lost Tickets" table after 7th column index

                  //     let skipColumns: number = this.isSingleLotSummary ? 5 : 7;

                  //     if (columnIndex < skipColumns) {
                  //       if (!nonValidatedByLotRow) {
                  //         nonValidatedByLotRow = {};
                  //       }
                  //       nonValidatedByLotRow[columnKey] = rowData[columnKey];
                  //     } else if (columnIndex > skipColumns) {
                  //       if (!validatedByLotRow) {
                  //         validatedByLotRow = {};
                  //       }
                  //       validatedByLotRow[columnKey] = rowData[columnKey];
                  //     }
                  //   });
                  //   if (nonValidatedByLotRow) {
                  //     nonValidatedByLotRowDataFiltered.push(nonValidatedByLotRow);
                  //   }
                  //   if (validatedByLotRow) {
                  //     validatedByLotRowDataFiltered.push(validatedByLotRow);
                  //   }
                  // }

                }

                //Remove Last row because its a total summary row so it should not consider as table data
                let nonValidatedByLotRows = nonValidatedByLotRowDataFiltered.slice(1, nonValidatedByLotRowDataFiltered.length);
                let validatedByLotRows = validatedByLotRowDataFiltered.slice(1, validatedByLotRowDataFiltered.length);

                let nvblRowsUpdatedKeys: any[] = [];
                let lastLotID: string = '';

                //Map column array with data
                nonValidatedByLotRows.forEach((rowData: any) => {
                  let row: any = {};

                  //Check if row is not blank
                  if (Object.values(rowData).join('') !== '') {
                    Object.keys(rowData).forEach((rowDataKeys: string, index: number) => {
                      const matchedColumnIndex = nonValidatedByLotColumnNames.findIndex((columnData: any) => columnData.original_key == rowDataKeys);
                      if (matchedColumnIndex !== -1) {
                        //Store lot id to use for grouped rows
                        if (nonValidatedByLotColumnNames[matchedColumnIndex]['field'] == "lot" && rowData[rowDataKeys]) {
                          lastLotID = rowData[rowDataKeys];
                        }
                        //If lot column is blank means this is grouped row in this case we need to use last lot id
                        if (nonValidatedByLotColumnNames[matchedColumnIndex]['field'] == "lot" && rowData[rowDataKeys] == '') {
                          row[nonValidatedByLotColumnNames[matchedColumnIndex]['field']] = lastLotID;
                        } else {
                          row[nonValidatedByLotColumnNames[matchedColumnIndex]['field']] = rowData[rowDataKeys];
                        }
                      }
                    })
                    nvblRowsUpdatedKeys.push(row);
                  }
                });

                //Remove sub total row as we dont want to show it in table
                const nvblRowsUpdatedRows = nvblRowsUpdatedKeys.filter((rowsData: any) => {
                  return !(rowsData['value'].includes("Total:") || rowsData['value'].includes("subtotal:"));
                });
                //Get sub total row to display in table footer
                const nvblSummaryRows = nvblRowsUpdatedKeys.filter((rowsData: any) => {
                  return rowsData['value'].includes("Total:");
                });

                this.fileImportData[this.fileImportData.length - 1]['non_validated_by_lot'] = {
                  columns: nonValidatedByLotColumnNames,
                  rows: nvblRowsUpdatedRows,
                  summary: nvblSummaryRows
                };

                let vblRowsUpdatedKeys: any[] = [];
                lastLotID = "";

                //Map column array with data
                validatedByLotRows.forEach((rowData: any) => {
                  let row: any = {};

                  //Check if row is not blank
                  if (Object.values(rowData).join('') !== '') {
                    Object.keys(rowData).forEach((rowDataKeys: string, index: number) => {
                      const matchedColumnIndex = validatedByLotColumnNames.findIndex((columnData: any) => columnData.original_key == rowDataKeys);
                      if (matchedColumnIndex !== -1) {
                        if (validatedByLotColumnNames[matchedColumnIndex]['field'] == "lot" && rowData[rowDataKeys]) {
                          lastLotID = rowData[rowDataKeys];
                        }
                        if (validatedByLotColumnNames[matchedColumnIndex]['field'] == "lot" && rowData[rowDataKeys] == '') {
                          row[validatedByLotColumnNames[matchedColumnIndex]['field']] = lastLotID;
                        } else {
                          row[validatedByLotColumnNames[matchedColumnIndex]['field']] = rowData[rowDataKeys];
                        }
                      }
                    })

                    if (!row['value']) {
                      // Normalize the data to ensure the required property is always present
                      row['value'] = '';
                    }

                    vblRowsUpdatedKeys.push(row);
                  }
                });

                const vblRowsUpdatedRows = vblRowsUpdatedKeys.filter((rowsData: any) => {
                  return !(rowsData['value'].includes("Total:") || rowsData['value'].includes("subtotal:"));
                });
                const vblSummaryRows = vblRowsUpdatedKeys.filter((rowsData: any) => {
                  return rowsData['value'].includes("Total:");
                });

                this.fileImportData[this.fileImportData.length - 1]['validated_by_lot'] = {
                  columns: validatedByLotColumnNames,
                  rows: vblRowsUpdatedRows,
                  summary: vblSummaryRows
                };

                nonValidatedByLotRowIndex = [];
              }

            })
          });

          this.fileImportData.forEach((data: any, index: number) => {
            if (data['ticket_summary'] && data['revenue']) {
              let summary_rows: any = [];
              data['ticket_summary']['rows'].forEach((ticket_summary_row: any) => {

                // Look for the position of...
                const indexPosition = summary_rows.findIndex((summary: any) => summary['cashier'] == ticket_summary_row['lot_name']);

                // Assume that we did not find the position
                let summaryfound = false;

                if (indexPosition !== -1) {
                  // Our assumption was wrong - we did find it
                  summaryfound = true;
                }

                if (!summaryfound) {
                  // console.log(`Did not find a summary`);
                  const summary_group_by_lot_name = data['ticket_summary']['rows'].filter((data: any) => data['lot_name'] == ticket_summary_row['lot_name']);
                  let tix_collected: number = 0;
                  let amount: number = 0;
                  let remaining_amount: number = 0;
                  let cc_fee: number = 0;
                  let nvbl_total: number = 0;
                  let vbl_total: number = 0;
                  let nvbl_total_qty: number = 0;
                  let vbl_total_qty: number = 0;
                  let short: number | undefined = undefined;

                  let stations: any[] = [];
                  let lost_tix: any[] = [];

                  const revenue_group_by_lot_name = data['revenue']['rows'].filter((data: any) => data['lot'] == ticket_summary_row['lot_name']);
                  if (data['non_validated_by_lot']) {
                    //For single lot import there no "Lot" column in "Non-Validated" table so lets calculate total directly without grouping
                    if (this.isSingleLotSummary) {
                      nvbl_total = data['non_validated_by_lot']['rows'].reduce((acc: number, item: any) => acc + Number(item.amount), 0);
                      nvbl_total_qty = data['non_validated_by_lot']['rows'].reduce((acc: number, item: any) => acc + Number(item.qty), 0);
                    } else {
                      const non_validated_by_lot_group_by_lot_name = data['non_validated_by_lot']['rows'].filter((data: any) => data['lot'] == ticket_summary_row['lot_name']);
                      nvbl_total = non_validated_by_lot_group_by_lot_name?.reduce((acc: number, item: any) => acc + Number(item.amount), 0);
                      nvbl_total_qty = non_validated_by_lot_group_by_lot_name?.reduce((acc: number, item: any) => acc + Number(item.qty), 0);
                    }
                  }
                  if (data['validated_by_lot']) {
                    //For single lot import there no "Lot" column in "Validated" table so lets calculate total directly without grouping
                    if (this.isSingleLotSummary) {
                      vbl_total = data['validated_by_lot']['rows'].reduce((acc: number, item: any) => acc + Number(item.amount), 0);
                      vbl_total_qty = data['validated_by_lot']['rows'].reduce((acc: number, item: any) => acc + Number(item.qty), 0);
                    } else {
                      const validated_by_lot_group_by_lot_name = data['validated_by_lot']['rows'].filter((data: any) => data['lot'] == ticket_summary_row['lot_name']);
                      vbl_total = validated_by_lot_group_by_lot_name?.reduce((acc: number, item: any) => acc + Number(item.amount), 0);
                      vbl_total_qty = validated_by_lot_group_by_lot_name?.reduce((acc: number, item: any) => acc + Number(item.qty), 0);
                    }
                  }

                  /**
                   * 23/07/2025 - Wasim
                   * If sheet has single lot but multiple stations we wont be able to determine which station has tickets count
                   * because single lot sheet dont have lot column in 'validated' and 'non-validated' table
                   * So we have checked if sheet and is single lot summary if Yes then we wont display count of tickets per station
                   * Also we got issue that 'Tickets Collected' in 'Daily Tickets Collected Summary' table is twice then sheet
                   * because we was doing addition in forEach loop which we have now removed to fix the bug
                   * We have keep old method of calculation in else part becasue its needed for multi location sheet
                  **/

                  if (this.isSingleLotSummary) {
                    summary_group_by_lot_name.forEach((summary_data: any) => {
                      const paid_tickets_count = (vbl_total_qty + nvbl_total_qty);
                      tix_collected = paid_tickets_count;
                      stations.push(summary_data['station_name'])
                    });
                  } else {
                    summary_group_by_lot_name.forEach((summary_data: any) => {
                      const paid_tickets_count = (vbl_total_qty + nvbl_total_qty);
                      tix_collected += paid_tickets_count;
                      stations.push(summary_data['station_name'] + ": " + paid_tickets_count)
                    });
                  }

                  if (this.dailySummaryCalculateRevenueMethod == "Use Validated and Non-Validated Income") {
                    remaining_amount = (nvbl_total + vbl_total);
                  } else if (this.dailySummaryCalculateRevenueMethod == "Use Revenue Summary") {
                    const dataRevenueSummary = data['revenue']['summary'];
                    const totalAmount = dataRevenueSummary.reduce((sum: number, item: any) => sum + parseFloat(item.amount), 0);
                    remaining_amount = totalAmount;
                  }

                  revenue_group_by_lot_name.forEach((summary_data: any) => {

                    // ‘Do Not Report Credit Card Fees' setting is set then: credit card fees won’t be reported on the DRE or subtracted from the revenue.
                    if (!this.dailySummaryDontReportCreditCardFees) {
                      remaining_amount = remaining_amount - Number(summary_data['fee']);
                    }

                    if (summary_data['payment_type'] == "Credit Card") {
                      cc_fee = Number(summary_data['fee']);
                    }
                    if (summary_data['payment_type'] == "Short") {
                      short = Number(summary_data['amount']);
                    }
                  });
                  amount = remaining_amount;

                  if (data['lost_tickets']) {
                    //For multiple lots, we want all lost tickets to get consider only for first shift (1549)
                    //Single lot "lost tickets" table will have unique name so lets check flag

                    const lot_shift = this.lotShiftMappping.find((lot: any) => lot['lot'] == ticket_summary_row['lot_name'] && lot['shift'] == 1);

                    if (lot_shift || this.isSingleLotSummary) {
                      data['lost_tickets']['summary'].forEach((summary_data: any) => {
                        //Tp prevent blank DRE entry we have checked if qty and amount has value then only push lost tickets data
                        if (summary_data['qty'] && summary_data['amount']) {
                          lost_tix = ['Qty: ' + summary_data['qty'], 'Amount: ' + summary_data['amount']];
                        }
                      });
                    }
                  }

                  const lot_shift = this.isSingleLotSummary ? 1 : this.lotShiftMappping.find((lot: any) => lot['lot'] == ticket_summary_row['lot_name']);

                  summary_rows.push({
                    shift: this.isSingleLotSummary ? lot_shift : lot_shift['shift'],
                    cashier: ticket_summary_row['lot_name'],
                    tix_collected: tix_collected,
                    amount: amount,
                    cc_fee: cc_fee,
                    stations: stations.join(", "),
                    lost_tix: lost_tix.join(", "),
                    short: short
                  });
                } else {
                  // console.log(`Found a summary`);
                }

              });
              //Sort ticket collected grid by shift
              this.dailySummaryData.rows = summary_rows.sort(function (a: any, b: any) { return a.shift - b.shift; });
            }
          })

          // console.log(this.fileImportData);
          if (this.isSingleLotSummary && this.dailySummaryData) {

            //When there are multiple lots But the location only has one shift then the cashier should be ‘Multiple Lots’
            if (this.dailySummaryData['rows'].length > 1) {

              let totalTickets = 0;
              let totalCCFee = 0;
              let entries: string[] = [];
              let amount: number = 0;
              let remaining_amount: number = 0;
              let nvbl_total: number = 0;
              let vbl_total: number = 0;
              let lost_tix: any[] = [];

              const daily_summary_rows = structuredClone(this.dailySummaryData['rows']);

              this.dailySummaryData['rows'] = [];

              //Get total amount and fees of all lots and add into single record
              daily_summary_rows.forEach((row: any) => {
                totalTickets = row.tix_collected;

                totalCCFee += row.cc_fee;

                const stationCleaned = row.stations.replace(" Entry", "");
                entries.push(stationCleaned);

                this.fileImportData.forEach((data: any, index: number) => {

                  nvbl_total = data['non_validated_by_lot']['rows'].reduce((acc: number, item: any) => acc + Number(item.amount), 0);
                  vbl_total = data['validated_by_lot']['rows'].reduce((acc: number, item: any) => acc + Number(item.amount), 0);

                  if (this.dailySummaryCalculateRevenueMethod == "Use Validated and Non-Validated Income") {
                    remaining_amount = (nvbl_total + vbl_total);
                  } else if (this.dailySummaryCalculateRevenueMethod == "Use Revenue Summary") {
                    const totalAmount = data['revenue']['summary'].reduce((sum: number, item: any) => sum + parseFloat(item.amount), 0);
                    remaining_amount = totalAmount;
                  }

                  amount = remaining_amount;

                  data['lost_tickets']['summary'].forEach((summary_data: any) => {
                    //Tp prevent blank DRE entry we have checked if qty and amount has value then only push lost tickets data
                    if (summary_data['qty'] && summary_data['amount']) {
                      lost_tix = ['Qty: ' + summary_data['qty'], 'Amount: ' + summary_data['amount']];
                    }
                  });

                });

              });

              //Add a row with sum of all shift fees and amount
              this.dailySummaryData['rows'].push({
                "shift": 1,
                "cashier": "Multiple Lots",
                "tix_collected": totalTickets,
                "amount": amount - totalCCFee,
                "cc_fee": Number(totalCCFee.toFixed(2)),
                "stations": "Entries:  " + entries.join(",  "),
                "lost_tix": lost_tix.join(", "),
              });

            }
          }
        } else if (worksheetData?.length && this.dailySummaryReportType == 'cashier') {
          this.importedSheetData = worksheetData;
        }

        this.loading = false;
      } catch (error: any) {
        // console.error('Failed to parse daily summary file:', error);
        console.log(error?.message)
        this.messageService.add({
          severity: "error",
          detail: error?.message || "Failed to parse daily summary file.",
        });
      } finally {
        this.loading = false;
      }

    }
  }

  /**
   * This function will check if table exist in xlsx file object
   * 
   * @param tableHeader string
   * @param worksheetData xlsx parsed object
   * @returns boolean
   */
  private checkTableExist(tableHeader: string, worksheetData: any) {
    if (!tableHeader) { return false; }

    let isTableFound: boolean = false;

    for (let index = 0; index < worksheetData.length; index++) {
      const worksheetDataRowsObj = worksheetData[index];

      for (let index = 0; index < Object.keys(worksheetDataRowsObj).length; index++) {
        const key = Object.keys(worksheetDataRowsObj)[index];
        // Get the current cell data for the current row
        const cellDataLowerCased: string = worksheetDataRowsObj[key].toLowerCase();

        //If table found then break this row loop as we dont need to check further
        if (cellDataLowerCased == tableHeader) {
          isTableFound = true;
          break;
        }
      }
      //If table found then break this loop as we dont need to check further
      if (isTableFound) {
        break;
      }
    }

    return isTableFound;
  }

  private updateWorksheetCellFormat(worksheet: any) {
    // To fix issue with currency type field we have changed format of cell
    Object.keys(worksheet).forEach((key, index) => {
      if (worksheet[key]['t'] == "n" && worksheet[key]['z'] == "[$-1010409]General") {
        worksheet[key]['z'] = "General";
      }
      if (worksheet[key]['t'] == "n" && worksheet[key]['z'] == "[$-409]General") {
        worksheet[key]['z'] = "General";
      }
      if (worksheet[key]['t'] == "n" && worksheet[key]['z'] == '[$-1010409]"$"#,##0.00;("$"#,##0.00)') {
        worksheet[key]['z'] = '0.00';
      }
      if (worksheet[key]['t'] == "n" && worksheet[key]['z'] == '[$-409]\\$#,##0.00;"($"#,##0.00\\)') {
        worksheet[key]['z'] = '0.00';
      }
    });

    return worksheet;
  }

  clearSheetSeletion(): void {
    this.messageService.clear();
    this.importedSheetData = undefined;
    this.fileImportData = [];
  }

  async importSummaryData(): Promise<void> {
    this.messageService.clear();

    // Normalize the possible rows imported otherwise assume zero
    const revenueRowsImportedCount: number = this.fileImportData[0]['revenue']['rows']?.length ?? 0;
    const dailySummaryDataRowsCount: number = this.dailySummaryData.rows?.length ?? 0;

    // We must have some revenue or summary data to allow the import
    if (dailySummaryDataRowsCount == 0 && revenueRowsImportedCount == 0) {
      this.messageService.add({
        severity: "error",
        detail: "No data found for import.",
      });
      return;
    }

    //
    // Data is present so do the import
    //

    const totalCCFee = this.dailySummaryData['rows'].reduce((sum: number, item: any) => sum + parseFloat(item['cc_fee']), 0);
    let dailySummaryDataToImport = structuredClone(this.dailySummaryData['rows']);

    let noTicketCollectedRevenue: any[] = [];
    this.fileImportData[0]['revenue']['rows'].forEach((row: any) => {

      //Skip summary row
      if (!row['lot'].includes("Credit Card payment") && !row['lot'].includes("Cash payment")) {
        //Check if revenue lot rows is exist in ticket summary
        const matchingLotIndex = this.fileImportData[0]['ticket_summary']['rows'].findIndex((tix_summary_row: any) => tix_summary_row['lot_name'] == row['lot']);

        //If no matching row found that means we still need to import this validated revenue
        if (matchingLotIndex === -1) {
          noTicketCollectedRevenue.push({
            amount: row['amount'],
            cashier: row['lot'],
            cc_fee: 0,
            lost_tix: "",
            shift: 1,
            stations: "",
            tix_collected: 0
          })
        }
      }
    });

    dailySummaryDataToImport = dailySummaryDataToImport.concat(noTicketCollectedRevenue);

    /**
     * Remove properties based on settings
     */
    dailySummaryDataToImport = dailySummaryDataToImport.map((dailySummary: any) => {
      if (dailySummary['lost_tix']) {

        /**
         * To skip storing lost tickets in DRE
         * Check `dailySummaryDontTrackLostTickets` setting is true means we dont need to pass this property
         *
         **/
        if (this.dailySummaryDontTrackLostTickets) {
          delete dailySummary['lost_tix'];
        } else {
          /**
           * Ok so settings is allowed to store lost tickets in DRE
           * But to skip blank DRE entry we have pass only if we have quantity and amount
           * */
          const [qty, amt] = dailySummary['lost_tix'].split(", ");
          if (qty && amt) {

            const quantity = this.getLostTicketQty(qty);
            const amount = this.getLostTicketAmount(amt);

            dailySummary['lost_tix'] = { qty: quantity, amount: amount };
          } else {
            /**
             * We dont have quantity and amount so we have delete this property to prevent blank DRE entry
             */
            delete dailySummary['lost_tix'];
          }
        }
      }

      /**
       * To skip storing crecit card fee in DRE
       * Check `dailySummaryDontReportCreditCardFees` setting is true means we dont need to pass this property
       */
      if (dailySummary['cc_fee'] && this.dailySummaryDontReportCreditCardFees) {
        delete dailySummary['cc_fee'];
      }
      return dailySummary;
    })
    let ticketSummaryDataToImport = structuredClone(this.fileImportData[0]['ticket_summary']['rows']);

    //Convert sequence string into object so API stores it correctly in separate table fields
    ticketSummaryDataToImport = ticketSummaryDataToImport.map((ticketSummary: any) => {
      const [sequence_start, sequence_end] = ticketSummary['sequence_#_start_-_end'].split(" - ");
      ticketSummary['sequence_#_start_-_end'] = { sequence_start: sequence_start, sequence_end: sequence_end };
      return ticketSummary;
    });

    //
    // Uncomment if you want to see what's sent without actually submitting the data
    //
    
    // return;

    const fileBase64: string = await this.blobToBase64(this.selectedFileBlobData!);
    const fileType: string = this.selectedFileBlobData!.type;
    const fileName: string = this.selectedFileBlobData!.name;

    await this.saveDailySummaryData({
      dailySummaryData: dailySummaryDataToImport,
      ticketSummaryData: ticketSummaryDataToImport,
      reportCCFeesToTicketsCollected: this.dailySummaryReportCCFeesToTicketsCollected,
      totalCCFee: totalCCFee,
      date: this.fileImportData[0]['date'],
      fileName: fileName,
      fileContent: fileBase64,
      fileType: fileType
    })

  }

  blobToBase64(blob: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob); // includes "data:*/*;base64," prefix
    });
  }

  async saveDailySummaryData(params: any) {
    this.messageService.clear();

    this.importSummaryInProgress = true;

    this.trpcService.trpc.lots.importDailySummaryData.mutate(params).then(async () => {
      // Track success event
      try {
        await this.trpcService.trpc.lots.trackDailySummaryEvent.mutate({
          eventType: 'import_completed',
          metadata: {
            dailySummaryRowCount: params.dailySummaryData?.length || 0,
            ticketSummaryRowCount: params.ticketSummaryData?.length || 0,
            timestamp: new Date().toISOString(),
            appVersion: environment.version
          }
        });
      } catch (error) {
        // Don't let tracking errors prevent showing success
        console.error('Failed to track success event:', error);
      }

      this.messageService.add({
        severity: "success",
        detail: "Daily summary data imported successfully.",
      });
      this.importSummaryInProgress = false;
    }).catch(async (error: any) => {
      // Track error event
      try {
        await this.trpcService.trpc.lots.trackDailySummaryEvent.mutate({
          eventType: 'import_failed',
          metadata: {
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
            appVersion: environment.version
          }
        });
      } catch (trackingError) {
        // Don't let tracking errors prevent showing the actual error
        console.error('Failed to track error event:', trackingError);
      }

      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.importSummaryInProgress = false;
    });
  }

  clearSummaryData(date: string): void {

    this.messageService.clear();

    this.importSummaryInProgress = true;
    this.trpcService.trpc.lots.clearDailySummaryData.mutate({
      date: date
    }).then(() => {
      this.messageService.add({
        severity: "success",
        detail: "Daily summary data cleared successfully.",
      });
      this.importSummaryInProgress = false;
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.importSummaryInProgress = false;
    });;
  }

  checkAccessToFeature() {
    this.trpcService.trpc.lots.checkAccessToFeature.mutate({
      feature: COMPONENT_FEATURE_NAME
    }).then((response: AccessCheckResult) => {

      if (response.result === 'denied') {
        this.messageService.add({
          severity: "error",
          detail: 'No access to ' + COMPONENT_FEATURE_NAME + ' feature',
        });
      }

    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: 'Unable to check access to ' + COMPONENT_FEATURE_NAME + ' feature.',
      });

    });
  }

  getLostTicketQty(qty: string): number {
    return Number(qty.toLowerCase().replace("qty: ", ""));
  }

  getLostTicketAmount(amt: string): number {
    return Number(amt.toLowerCase().replace("amount: ", ""));
  }

  isRowNotEmpty(row: Record<string, any>): boolean {
    // Returns true if at least one property has a non-empty, non-null, non-undefined value
    return Object.values(row).some(value => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    });
  }
}
